
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, getDocs, query, where, documentId } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { getFunctions } from 'firebase/functions';
import { getAnalytics } from 'firebase/analytics';
import { getPerformance } from 'firebase/performance';
import { getMessaging, isSupported as isMessagingSupported } from 'firebase/messaging';


// Validate required environment variables
const requiredEnvVars = [
  'REACT_APP_FIREBASE_API_KEY',
  'REACT_APP_FIREBASE_AUTH_DOMAIN',
  'REACT_APP_FIREBASE_PROJECT_ID',
  'REACT_APP_FIREBASE_STORAGE_BUCKET',
  'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
  'REACT_APP_FIREBASE_APP_ID'
];

const missingVars = requiredEnvVars.filter(varName => !process.env[varName]);

if (missingVars.length > 0) {
  const errorMsg = `❌ Missing required Firebase environment variables: ${missingVars.join(', ')}. Please create a .env file in the project root with the required Firebase configuration.`;
  // In development, throw an error to prevent silent failures
  if (process.env.NODE_ENV !== 'production') {
    throw new Error(errorMsg);
  }
}

// Firebase configuration - loaded from environment ONLY (NEVER hardcode)
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || ''
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);
export const analytics = getAnalytics(app);
export const performance = getPerformance(app);

// Initialize Firebase Cloud Messaging (synchronously to avoid race conditions)
let messaging = null;
try {
  // Only initialize in browser environment
  if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
    messaging = getMessaging(app);
    console.log('✅ Firebase Messaging initialized successfully');
  } else {
    console.log('⚠️ Service workers not supported, messaging disabled');
  }
} catch (error) {
  console.warn('⚠️ Firebase Messaging not supported in this browser:', error.message);
  messaging = null;
}
export { messaging };


// Initialize App Check with reCAPTCHA v3 for production
if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY) {
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(
        process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY
      ),
      isTokenAutoRefreshEnabled: true
    });
  } catch (error) {
    // App Check initialization failed
  }
}

// Fetch listings by IDs from Firestore
export async function getListingsByIds(listingIds) {
  if (!listingIds || !Array.isArray(listingIds) || listingIds.length === 0) {
    return [];
  }

  if (listingIds.length > 10) {
    // Limiting to 10 listings
  }

  try {
    const batches = [];
    for (let i = 0; i < listingIds.length; i += 10) {
      const batchIds = listingIds.slice(i, i + 10);
      const q = query(collection(db, 'rooms'), where(documentId(), 'in', batchIds));
      batches.push(getDocs(q));
    }

    const results = await Promise.all(batches);
    return results.flatMap(snapshot => snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  } catch (error) {
    throw new Error('Failed to fetch listings');
  }
}

// Compare listings (basic: price, type, amenities)
export function compareListings(listings) {
  if (!listings || listings.length < 2) return null;

  try {
    const prices = listings.map(l => l.price || 0);
    const types = [...new Set(listings.map(l => l.type))];
    const allAmenities = listings.map(l => Array.isArray(l.amenities) ? l.amenities : []);
    let commonAmenities = [];
    if (allAmenities.length > 0) {
      commonAmenities = allAmenities.reduce((a, b) => a.length === 0 ? b : a.filter(x => b.includes(x)));
    }
    return {
      priceRange: {
        min: Math.min(...prices),
        max: Math.max(...prices),
        average: prices.length ? Math.round(prices.reduce((a, b) => a + b, 0) / prices.length) : 0
      },
      roomTypes: types,
      commonAmenities,
    };
  } catch (error) {
    return null;
  }
}

export default app;
